import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import {
  OptionSelect,
  SearchSelect,
} from '../../../../shared/components/search-select/search-select';
import {
  ServiceActivity,
  ServiceActivityDTO,
} from '../../../../core/models/serviceactivity.interface';
import { Address } from '../../../../core/models/address.interface';
import { Line } from '../../../../core/models/line.interface';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { LineService } from '../../../../core/services/line/line';
import { LoadingService } from '../../../../core/services/loading/loading';
import { AddressService } from '../../../../core/services/address/address-service';
import { LocationService } from '../../../../core/services/location/location-service';
import { ServiceactivityService } from '../../../../core/services/serviceactivity/serviceactivity-service';
import { FormUtils } from '../../../../shared/utils';
import { switchMap } from 'rxjs';
import { Location } from '../../../../core/models/location.interface';
import { HttpErrorResponse } from '@angular/common/http';
import { AssistanceserviceService } from '../../../../core/services/assistanceservice/assistanceservice-service';
import { AssistanceService } from '../../../../core/models/assistanceservice.interface';
import { MatOptionModule } from '@angular/material/core';
import { SearchfilterPipe } from '../../../../shared/pipes/searchfilter.pipe';
import { MatSelectModule } from '@angular/material/select';
import { TermPipe } from '../../../../shared/pipes/term.pipe';
import { TermKey } from '../../../../core/models/terminology.interface';

@Component({
  selector: 'app-serviceactivity-form',
  imports: [
    MatFormField,
    ReactiveFormsModule,
    MatLabel,
    MatInput,
    MatError,
    MatIcon,
    MatIconModule,
    FormsModule,
    SearchSelect,
    MatOptionModule,
    MatSelectModule,
    SearchfilterPipe,
    TermPipe,
  ],
  templateUrl: './serviceactivity-form.html',
  styleUrl: './serviceactivity-form.scss',
})
export class ServiceactivityForm {
  private dialogData = inject(MAT_DIALOG_DATA, { optional: true });
  fromList = this.dialogData?.fromList ?? false;
  serviceActivity: string | null = null;
  errorMsg: string[] | null = null;
  termKey = TermKey;
  serviceActivitySelected: ServiceActivity | null = null;
  assistanceService: AssistanceService | null = null;

  lineSelected: OptionSelect[] = [];
  listLine: Line[] = [];
  selectLine: OptionSelect | null = null;
  lineSeleccionada: Line | null = null;

  locationSelected: OptionSelect[] = [];
  listLocation: Location[] = [];
  selectLocation: OptionSelect | null = null;
  locationSeleccionado: Location | null = null;

  addressSelected: OptionSelect[] = [];
  listAddress: Address[] = [];
  selectAddress: OptionSelect | null = null;
  addressSeleccionado: Address | null = null;

  assistanceServiceSelected: OptionSelect[] = [];
  listAssistanceService: AssistanceService[] = [];
  selectAssistanceService: OptionSelect | null = null;
  assistanceServiceSeleccionado: AssistanceService | null = null;

  private formBuilder = inject(FormBuilder);
  searchControl = new FormControl();

  constructor(
    private dialogRef: MatDialogRef<ServiceactivityForm>,
    private toastr: ToastrService,
    private lineService: LineService,
    private loadingService: LoadingService,
    private addressService: AddressService,
    private serviceactivityService: ServiceactivityService,
    private locationService: LocationService,
    private assistanceserviceService: AssistanceserviceService,
  ) {}

  ngOnInit(): void {
    this.fetchData();
    this.serviceactivityService.asyncErrorValidation$.subscribe((error) => {
      if (error) {
        this.errorMsg = FormUtils.parseErrorMsgs(error);
      }
    });
    this.assistanceserviceService.selectedAssistanceService$.subscribe((assistanceService) => {
      this.assistanceService = assistanceService;
      this.serviceActivityForm.get('assistenceService')?.setValue(this.assistanceService?.id || 0);
    });
  }

  private fetchData() {
    this.lineService
      .getLines()
      .pipe(
        switchMap((lines) => {
          this.listLine = lines;
          this.lineSelected = this.lineService.getOptionLine(lines);
          return this.addressService.getAddresses();
        }),
        switchMap((addresses) => {
          this.listAddress = addresses;
          this.addressSelected = this.addressService.getOptionAddress(addresses);
          return this.assistanceserviceService.getAssistanceServices();
        }),
        switchMap((assistanceService) => {
          this.listAssistanceService = assistanceService;
          this.assistanceServiceSelected =
            this.assistanceserviceService.getOptionAssistanceService(assistanceService);
          return this.locationService.getLocations();
        }),
        switchMap((locations) => {
          this.listLocation = locations;
          this.locationSelected = this.locationService.getOptionLocations(locations);
          return this.serviceactivityService.selectedServiceActivity$;
        }),
      )
      .subscribe((serviceActivity) => {
        if (serviceActivity) {
          this.serviceActivity = String(serviceActivity.id);
          this.serviceActivitySelected = serviceActivity;
          this.serviceActivityForm.patchValue({
            ...this.serviceActivitySelected,
            locations: serviceActivity.locations.map((location) => location.id),
            adress: this.serviceActivitySelected.adress?.id || 0,
            line: this.serviceActivitySelected.line?.id || 0,
            consumableAssets: this.serviceActivitySelected.consumableAssets?.id || 0,
            assistenceService: this.serviceActivitySelected.assistenceService?.id || 0,
          });
          const adressOpt = this.addressSelected.find((el) => {
            if (this.serviceActivitySelected) {
              return el.value === this.serviceActivitySelected.adress?.id;
            }
            return false;
          });

          const assistenceServiceOpt = this.assistanceServiceSelected.find((el) => {
            if (this.serviceActivitySelected) {
              return el.value === this.serviceActivitySelected.assistenceService?.id;
            }
            return false;
          });

          const lineOpt = this.lineSelected.find((el) => {
            if (this.serviceActivitySelected) {
              return el.value === this.serviceActivitySelected.line?.id;
            }
            return false;
          });

          if (typeof assistenceServiceOpt !== 'undefined') {
            this.selectAssistanceService = assistenceServiceOpt;
          }
          if (typeof adressOpt !== 'undefined') {
            this.selectAddress = adressOpt;
          }
          if (typeof lineOpt !== 'undefined') {
            this.selectLine = lineOpt;
          }
        }
      });
  }

  serviceActivityForm = this.formBuilder.group({
    name: ['', [Validators.required, Validators.maxLength(150)]],
    description: ['', []],
    month: [0, [Validators.required]],
    consumableAssets: [0, [Validators.required]],
    locations: this.formBuilder.control<number[]>([], Validators.required),
    adress: [0, [Validators.required]],
    line: [0, [Validators.required]],
    assistenceService: [0, [Validators.required]],
  });

  getAssistenceService(id: number) {
    const index = this.listAddress.findIndex((assistenceService) => assistenceService.id === id);
    this.assistanceServiceSeleccionado = this.listAssistanceService[index];
  }

  onAssistenceServiceSelected(assistenceService: OptionSelect) {
    this.serviceActivityForm.get('assistenceService')?.setValue(assistenceService.value as number);
    this.getAssistenceService(assistenceService.value as number);
  }

  getAddress(id: number) {
    const index = this.listAddress.findIndex((address) => address.id === id);
    this.addressSeleccionado = this.listAddress[index];
  }

  onAddressSelected(address: OptionSelect) {
    this.serviceActivityForm.get('adress')?.setValue(address.value as number);
    this.getAddress(address.value as number);
  }

  getLine(id: number) {
    const index = this.listLine.findIndex((line) => line.id === id);
    this.lineSeleccionada = this.listLine[index];
  }

  onLinesSelected(line: OptionSelect) {
    this.serviceActivityForm.get('line')?.setValue(line.value as number);
    this.getLine(line.value as number);
  }

  closeDialog() {
    this.dialogRef.close();
  }

  saveServiceActivityForm() {
    const payload = this.serviceActivityForm.value as ServiceActivityDTO;
    if (this.serviceActivityForm.status === 'VALID' && !this.serviceActivity) {
      this.loadingService.show(true);
      this.serviceactivityService.createServiceActivity(payload).subscribe({
        next: (serviceActivity) => {
          this.loadingService.show(false);
          const msg = `El servicio de asistencia ${serviceActivity.name} fue CREADO correctamente!`;
          this.toastr.success(msg, 'Exitoso!', {
            progressBar: true,
            timeOut: 5000,
          });
          this.dialogRef.close();
        },
        error: ({}: HttpErrorResponse) => {
          if (this.errorMsg) {
            this.loadingService.show(false);
            this.toastr.error('Error: ' + this.errorMsg, undefined, {
              progressBar: true,
              timeOut: 5000,
            });
          }
        },
      });
    } else if (this.serviceActivityForm.status === 'VALID' && this.serviceActivity) {
      this.loadingService.show(true);
      const id = Number(this.serviceActivity);
      this.serviceactivityService.updateServiceActivity(id, { ...payload, id: id }).subscribe({
        next: (serviceActivity) => {
          this.loadingService.show(false);
          const msg = `El servicio de asistencia ${serviceActivity.name} fue EDITADO correctamente!`;
          this.toastr.success(msg, 'Exitoso!', {
            progressBar: true,
            timeOut: 5000,
          });
          this.dialogRef.close();
        },
        error: ({}: HttpErrorResponse) => {
          if (this.errorMsg) {
            this.loadingService.show(false);
            this.toastr.error('Error: ' + this.errorMsg, undefined, {
              progressBar: true,
              timeOut: 5000,
            });
          }
        },
      });
    }
  }
  getLineValue() {
    return this.selectLine;
  }

  getAddressValue() {
    return this.selectAddress;
  }

  getassistanceServiceValue() {
    return this.selectAssistanceService;
  }
}
